import * as React from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  makeStyles
} from "@material-ui/core";
import { Close as CloseIcon } from "@material-ui/icons";
import { FastField, FieldProps, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { ContainedButton, DefaultButton } from "src/common/components";
import { ICancelTalkPayload, ITalkCancelationReason } from "../types";

const useStyles = makeStyles((theme: any) => ({
  closeButton: {
    color: theme.palette.grey[500],
    position: "absolute" as "absolute",
    right: 8,
    top: 8
  },
  container: {
    maxWidth: "100%",
    width: 450
  },
  submitBtn: {
    minWidth: 0
  }
}));

interface IProps {
  open: boolean;
  onClose: () => void;
  onCancel: (data: ICancelTalkPayload) => void;
  cancelReasons: ITalkCancelationReason[];
}

const CancelModal = ({ onClose, open, onCancel, cancelReasons }: IProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const handleSubmit = React.useCallback(
    ({ code, comment }: ICancelTalkPayload) => {
      onCancel({
        code: parseInt(code as string, 10),
        comment: code === "4" ? comment : undefined
      });

      // TODO
      onClose();
    },
    [onCancel]
  );
  return (
    <Dialog onClose={onClose} open={open}>
      <DialogTitle>
        {t(`feedback.page.my-employees.cancelModal.title`)}
        {onClose ? (
          <IconButton className={classes.closeButton} aria-label="close" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        ) : null}
      </DialogTitle>
      <Formik
        onSubmit={handleSubmit}
        initialValues={React.useMemo(
          () => ({
            code: null
          }),
          []
        )}
      >
        {({ handleSubmit: onSubmit, values }) => {
          return (
            <form onSubmit={onSubmit} className={classes.container}>
              <DialogContent>
                <Grid spacing={2} container>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <FormLabel>{t(`feedback.page.my-employees.reopenModal.chooseReason`)}</FormLabel>
                      <FastField name="code">
                        {({ field }: FieldProps) => {
                          return (
                            <RadioGroup {...field}>
                              {cancelReasons.map(({ code, phraseAppKey }) => {
                                return (
                                  <FormControlLabel
                                    key={code}
                                    value={code.toString()}
                                    label={t(phraseAppKey)}
                                    control={<Radio required color="primary" />}
                                  />
                                );
                              })}
                            </RadioGroup>
                          );
                        }}
                      </FastField>
                    </FormControl>
                  </Grid>
                  {values.code === "4" ? (
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <FormLabel>{t(`feedback.page.my-employees.reopenModal.comment`)}</FormLabel>
                        <FastField name="comment">
                          {({ field }: FieldProps) => {
                            return (
                              <TextField
                                required
                                placeholder={t(`feedback.page.my-employees.reopenModal.comment.placeholder`)}
                                multiline
                                {...field}
                              />
                            );
                          }}
                        </FastField>
                      </FormControl>
                    </Grid>
                  ) : null}
                </Grid>
              </DialogContent>
              <DialogActions>
                <DefaultButton type="button" onClick={onClose}>
                  {t(`feedback.page.my-employees.cancelModal.close`)}
                </DefaultButton>
                <ContainedButton className={classes.submitBtn} type="submit" size="small">
                  {t(`feedback.page.my-employees.cancelModal.submit`)}
                </ContainedButton>
              </DialogActions>
            </form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default React.memo(CancelModal);
